import React, { useState, useEffect } from 'react';
import { useRole } from '../../hooks/useRole';
import Icon from '../common/Icons';
import { getRoleLabel } from '../../constants/roles';
import { formatDate } from '../../utils/dateFormatter';
import { formatUserDisplayName } from './userListUtils';
import { AdminModal, ModalDetailField, ModalFooterActions } from './ui';

function getUserInitials(user) {
  const last = (user?.last_name || '').trim();
  const first = (user?.first_name || '').trim();
  if (last && first) return `${last[0]}${first[0]}`.toUpperCase();
  if (last) return last.slice(0, 2).toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (user?.username) return user.username.slice(0, 2).toUpperCase();
  return 'U';
}

const UserDetails = ({ show, onHide, user, onEdit, onDelete, onToggleStatus }) => {
  const { canManageUsers } = useRole();
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    if (!show) setConfirmAction(null);
  }, [show, user?.id]);

  if (!user) return null;

  const displayName = formatUserDisplayName(user);
  const initials = getUserInitials(user);

  const handleEdit = () => {
    onEdit(user);
    onHide();
  };

  const handleConfirmDelete = () => {
    onDelete(user.id, { confirmed: true });
    onHide();
  };

  const handleConfirmToggle = () => {
    onToggleStatus(user.id, !user.is_active);
    onHide();
  };

  const confirmCopy =
    confirmAction === 'delete'
      ? {
          variant: 'danger',
          title: 'Supprimer ce compte ?',
          message: `L'utilisateur ${displayName} sera définitivement supprimé. Cette action est irréversible.`,
          confirmLabel: 'Supprimer',
          onConfirm: handleConfirmDelete
        }
      : confirmAction === 'toggle'
        ? {
            variant: 'warn',
            title: user.is_active ? 'Désactiver ce compte ?' : 'Activer ce compte ?',
            message: user.is_active
              ? `${displayName} ne pourra plus se connecter tant que le compte est inactif.`
              : `${displayName} retrouvera l'accès à la plateforme.`,
            confirmLabel: user.is_active ? 'Désactiver' : 'Activer',
            onConfirm: handleConfirmToggle
          }
        : null;

  return (
    <AdminModal
      show={show}
      onHide={onHide}
      size="lg"
      icon="user"
      badge="Fiche utilisateur"
      title={displayName}
      subtitle={user.username ? `@${user.username} · ID #${user.id}` : `ID #${user.id}`}
      footer={
        confirmCopy ? (
          <ModalFooterActions
            onCancel={() => setConfirmAction(null)}
            cancelLabel="Retour"
            primary={
              <button
                type="button"
                className={`adm-modal-btn adm-modal-btn--${confirmCopy.variant}`}
                onClick={confirmCopy.onConfirm}
              >
                <Icon name={confirmAction === 'delete' ? 'trash' : user.is_active ? 'pause' : 'play'} size={14} />
                {confirmCopy.confirmLabel}
              </button>
            }
          />
        ) : (
          <div className="adm-modal-footer-split">
            {canManageUsers() ? (
              <div className="adm-modal-footer-split__danger">
                <button
                  type="button"
                  className="adm-modal-btn adm-modal-btn--ghost"
                  onClick={() => setConfirmAction(user.is_active ? 'toggle' : 'toggle')}
                  style={{ color: user.is_active ? '#b45309' : '#15803d', borderColor: 'currentColor' }}
                >
                  <Icon name={user.is_active ? 'pause' : 'play'} size={14} />
                  {user.is_active ? 'Désactiver' : 'Activer'}
                </button>
                <button
                  type="button"
                  className="adm-modal-btn adm-modal-btn--ghost"
                  onClick={() => setConfirmAction('delete')}
                  style={{ color: '#dc2626', borderColor: 'rgba(220,38,38,0.35)' }}
                >
                  <Icon name="trash" size={14} />
                  Supprimer
                </button>
              </div>
            ) : null}
            <div className="adm-modal-footer-split__main">
              <button type="button" className="adm-modal-btn adm-modal-btn--ghost" onClick={onHide}>
                Fermer
              </button>
              {canManageUsers() && (
                <button type="button" className="adm-modal-btn adm-modal-btn--primary" onClick={handleEdit}>
                  <Icon name="edit" size={14} />
                  Modifier
                </button>
              )}
            </div>
          </div>
        )
      }
    >
      {confirmCopy && (
        <div className={`adm-modal-confirm ${confirmCopy.variant === 'warn' ? 'adm-modal-confirm--warn' : ''}`}>
          <span className="adm-modal-confirm__icon" aria-hidden>
            <Icon name={confirmAction === 'delete' ? 'alertTriangle' : 'infoCircle'} size={18} />
          </span>
          <div>
            <strong>{confirmCopy.title}</strong>
            <p className="mb-0 mt-1">{confirmCopy.message}</p>
          </div>
        </div>
      )}

      <div className="adm-modal-hero">
        <div className="adm-modal-hero__avatar" aria-hidden>
          {initials}
        </div>
        <div className="adm-modal-hero__main">
          <h3 className="adm-modal-hero__title">{displayName}</h3>
          <p className="adm-modal-hero__sub">
            {user.email || user.phone || 'Aucun contact renseigné'}
          </p>
          <div className="adm-modal-hero__badges">
            <span className="adm-modal-type">{getRoleLabel(user.role)}</span>
            <span className={`adm-modal-status ${user.is_active ? 'adm-modal-status--active' : 'adm-modal-status--inactive'}`}>
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: 'currentColor',
                  display: 'inline-block'
                }}
              />
              {user.is_active ? 'Actif' : 'Inactif'}
            </span>
          </div>
        </div>
      </div>

      <div className="adm-modal-detail">
        <ModalDetailField label="Nom d'utilisateur">
          {user.username ? (
            <span className="fw-semibold">@{user.username}</span>
          ) : (
            <span className="text-muted">Non renseigné</span>
          )}
        </ModalDetailField>

        <ModalDetailField label="Email">
          {user.email || <span className="text-muted">Non renseigné</span>}
        </ModalDetailField>

        <ModalDetailField label="Téléphone">
          {user.phone || <span className="text-muted">Non renseigné</span>}
        </ModalDetailField>

        <ModalDetailField label="Rôle">
          <span className="adm-modal-type">{getRoleLabel(user.role)}</span>
        </ModalDetailField>

        <ModalDetailField label="Sites de travail" fullWidth>
          {Array.isArray(user.work_sites) && user.work_sites.length > 0 ? (
            <div className="d-flex flex-wrap gap-2">
              {user.work_sites.map((site) => (
                <span
                  key={site.id}
                  className={`adm-modal-site-tag ${site.is_active ? '' : 'adm-modal-site-tag--inactive'}`}
                >
                  {site.name}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-muted">Aucun site affecté</span>
          )}
        </ModalDetailField>

        <div className="adm-modal-detail__meta">
          <ModalDetailField label="Créé le">
            <span className="text-muted">{formatDate(user.created_at)}</span>
          </ModalDetailField>
          <ModalDetailField label="Dernière modification">
            <span className="text-muted">{formatDate(user.updated_at)}</span>
          </ModalDetailField>
        </div>
      </div>
    </AdminModal>
  );
};

export default UserDetails;
